import { useState, useRef, useCallback, useEffect } from "react";

interface UseScrollHighlightOptions {
  timeout?: number;
}

export function useScrollHighlight(options?: UseScrollHighlightOptions) {
  const timeout = options?.timeout ?? 2000;
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<number | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const cancelPending = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const clear = useCallback(() => {
    cancelPending();
    setHighlightedId(null);
  }, [cancelPending]);

  const trigger = useCallback(
    (id: string, scrollElementId?: string) => {
      cancelPending();

      setHighlightedId(null);
      rafRef.current = requestAnimationFrame(() => {
        if (!mountedRef.current) return;
        rafRef.current = null;

        setHighlightedId(id);

        if (scrollElementId) {
          const el = document.getElementById(scrollElementId);
          if (el) {
            const prefersReducedMotion = window.matchMedia(
              "(prefers-reduced-motion: reduce)",
            ).matches;
            el.scrollIntoView({
              behavior: prefersReducedMotion ? "auto" : "smooth",
              block: "center",
            });
          }
        }

        timeoutRef.current = setTimeout(() => {
          if (!mountedRef.current) return;
          setHighlightedId(null);
          timeoutRef.current = null;
        }, timeout);
      });
    },
    [timeout, cancelPending],
  );

  const isHighlighted = useCallback(
    (id: string) => highlightedId === id,
    [highlightedId],
  );

  return { highlightedId, trigger, clear, isHighlighted };
}
